// Building options inside the envelope.
//
// A typology fixes a ground footprint; each storey is that footprint
// intersected with the envelope at its own height. Facades therefore stay
// vertical except where a height-dependent setback forces a step back, which
// is how a real building is drawn, rather than every floor shrinking a little.
//
// Every option is capped three ways: POT on the ground footprint, CUT on the
// total above-ground area, and the envelope's own height. The caps are
// checked here, by construction, and again in the compliance report.
import { area, difference, edges, inset, intersection, union, strip, type MultiPoly } from "./geom.ts";
import type { Envelope, EnvelopeStorey } from "./envelope.ts";
import { balconyAllowance, limits } from "./indicators.ts";
import { CORRIDOR_FROM } from "./layout.ts";
import type { Programme, Rules } from "./rules.ts";

export type Typology = "bar" | "L" | "U" | "perimeter" | "tower";

/** Sides tried for a point block (bloc punct), metres: four corner flats a floor round a central core. */
export const TOWER_SIDES = [18, 20, 22];

/**
 * A point block: a square of the given side, square to the longest plot
 * edge, centred on the envelope. It must fit the envelope whole, or it is
 * not offered: a tower trimmed by a setback is no longer a tower.
 */
function tower(env: Envelope, at: EnvelopeStorey, side: number): MultiPoly {
  const ring = env.plot[0][0];
  const e = edges(ring);
  const [a, b] = e.reduce((best, x) => (Math.hypot(x[1][0] - x[0][0], x[1][1] - x[0][1]) > Math.hypot(best[1][0] - best[0][0], best[1][1] - best[0][1]) ? x : best));
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const u = [(b[0] - a[0]) / len, (b[1] - a[1]) / len], n = [-u[1], u[0]];
  const pts = at.footprint.flatMap((p) => p[0]);
  const c = [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length];
  const h = side / 2;
  const P = (ss: number, tt: number): [number, number] => [c[0] + u[0] * ss + n[0] * tt, c[1] + u[1] * ss + n[1] * tt];
  const sq: MultiPoly = [[[P(-h, -h), P(h, -h), P(h, h), P(-h, h)]]];
  return area(difference(sq, at.footprint)) < 0.5 ? sq : [];
}

export interface Storey {
  index: number;
  base: number;
  cornice: number;
  footprint: MultiPoly;
  area: number;
}

export interface Option {
  id: string;
  typology: Typology;
  /** Plot edges the wings sit along. */
  along: number[];
  depth: number;
  /** Per wing, how far from its plot edge the wing reaches (setback + depth). */
  reach: number[];
  storeys: Storey[];
  footprintArea: number; // Ac
  grossArea: number; // Adc above ground
  pot: number; // %
  cut: number;
  height: number;
  /** What stopped the building growing. */
  limitedBy: "cut" | "envelope";
}

/** Wings of a given depth along the given plot edges, inside one envelope storey. */
function wings(env: Envelope, at: EnvelopeStorey, along: number[], depth: number): MultiPoly {
  const e = edges(env.plot[0][0]);
  const parts = along.map((i) => {
    const [a, b] = e[i];
    return intersection(at.footprint, strip(a, b, 0, at.setbacks[i] + depth));
  });
  return union(...parts);
}

/** Narrowest wing worth building: below this a flat cannot face one side only. */
export const MIN_DEPTH = 9;
/** Shortest plot edge a wing may stand on. */
export const MIN_WING_EDGE = 8;

type Candidate = { typology: Typology; along: number[]; depth: number; footprint: MultiPoly };

function shape(env: Envelope, at: EnvelopeStorey, typology: Typology, along: number[], depth: number): MultiPoly {
  if (typology === "tower") return tower(env, at, depth);
  if (typology !== "perimeter") return wings(env, at, along, depth);
  // A courtyard narrower than the wings around it is a light well, not a
  // courtyard: require it to be at least as wide as two wings are deep.
  const ground = at.footprint;
  const court = inset(ground, depth);
  return area(inset(court, depth / 2)) > 0 ? difference(ground, court) : [];
}

/**
 * Each typology at every listed depth that fits under POT. When none does,
 * the deepest wing that still fits, found by bisection down to MIN_DEPTH:
 * POT is what usually binds a U or a courtyard block, and dropping them
 * would leave a shortlist of bars.
 */
function candidates(env: Envelope, at: EnvelopeStorey, rules: Rules, prog: Programme, maxProjection: number): Candidate[] {
  // Under CATUC the balconies the flats will get count in POT, so the
  // footprint must leave room for them.
  const projected = (fp: MultiPoly, depth: number) => area(fp) * (1 + balconyAllowance(rules, prog, depth, depth >= CORRIDOR_FROM));
  const maxFootprint = maxProjection;
  const ring = env.plot[0][0];
  const n = ring.length;
  // Wings stand only on edges long enough to carry one. A surveyed plot has
  // short jogs that would otherwise each spawn a bar, an L and a U, and the
  // L of two consecutive *long* edges is the L that matters.
  const long = [...Array(n).keys()].filter((i) => {
    const [a, b] = [ring[i], ring[(i + 1) % n]];
    return Math.hypot(b[0] - a[0], b[1] - a[1]) >= MIN_WING_EDGE;
  });
  const m = long.length;
  const layouts: { typology: Typology; along: number[] }[] = [];
  for (let k = 0; k < m; k++) {
    layouts.push({ typology: "bar", along: [long[k]] });
    if (m >= 2) layouts.push({ typology: "L", along: [long[k], long[(k + 1) % m]] });
    if (m >= 4) layouts.push({ typology: "U", along: [long[k], long[(k + 1) % m], long[(k + 2) % m]] });
  }
  layouts.push({ typology: "perimeter", along: long.length ? long : [...Array(n).keys()] });
  layouts.push({ typology: "tower", along: [] });

  const out: Candidate[] = [];
  for (const l of layouts) {
    let fitted = 0;
    for (const depth of l.typology === "tower" ? TOWER_SIDES : prog.depths) {
      const footprint = shape(env, at, l.typology, l.along, depth);
      const a = area(footprint);
      if (a > 0 && projected(footprint, depth) <= maxFootprint) { out.push({ ...l, depth, footprint }); fitted++; }
    }
    if (fitted > 0 || l.typology === "tower") continue;
    let lo = MIN_DEPTH, hi = Math.min(...prog.depths);
    const fits = (d: number) => { const fp = shape(env, at, l.typology, l.along, d); return area(fp) > 0 && projected(fp, d) <= maxFootprint; };
    if (!fits(lo)) continue;
    for (let k = 0; k < 12; k++) { const mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
    const depth = Math.floor(lo * 10) / 10;
    out.push({ ...l, depth, footprint: shape(env, at, l.typology, l.along, depth) });
  }
  return out;
}

export function options(env: Envelope, rules: Rules, prog: Programme, limit = 5): Option[] {
  if (env.storeys.length === 0) return [];
  const { maxProjection, maxGross } = limits(rules, env.plotArea);
  const built: Option[] = [];

  // The main body rises with vertical facades, set back as far as its own
  // full height requires; only the top `setbackStoreys` step back further.
  // Applying an H/2 rule storey by storey instead gives a ziggurat with a
  // 1.5 m terrace on every floor, which complies and which nobody builds.
  for (let body = 1; body <= env.storeys.length; body++) {
    const top = env.storeys[body - 1];
    for (const c of candidates(env, top, rules, prog, maxProjection)) {
      const ac = area(c.footprint);
      if (ac < 50) continue;
      const storeys: Storey[] = [];
      let gross = 0;
      let limitedBy: Option["limitedBy"] = "envelope";
      const last = Math.min(env.storeys.length, body + prog.setbackStoreys);
      for (const s of env.storeys.slice(0, last)) {
        const fp = s.index < body ? c.footprint : intersection(c.footprint, s.footprint);
        const a = area(fp);
        if (a < 25) break;
        if (gross + a > maxGross + 1e-6) { limitedBy = "cut"; break; }
        gross += a;
        storeys.push({ index: s.index, base: s.base, cornice: s.cornice, footprint: fp, area: a });
      }
      if (storeys.length < body) continue; // CUT stopped the body itself: a lower body covers it
      built.push({
        id: `${c.typology}-${c.along.join("")}-${c.depth}-${body}`,
        typology: c.typology,
        along: c.along,
        depth: c.depth,
        reach: c.along.map((i) => top.setbacks[i] + c.depth),
        storeys,
        footprintArea: ac,
        grossArea: gross,
        pot: (ac / env.plotArea) * 100,
        cut: gross / env.plotArea,
        height: storeys[storeys.length - 1].cornice,
        limitedBy,
      });
    }
  }

  // Best per typology first, so the shortlist offers different buildings
  // rather than five near-identical bars, then fill with the next best.
  built.sort((a, b) => b.grossArea - a.grossArea || a.id.localeCompare(b.id));
  const picked: Option[] = [];
  // On a narrow plot an L, a U and a bar can all fill the same envelope:
  // same footprint, same floor area, three names. That is one option.
  const same = (a: Option, b: Option) =>
    Math.abs(a.grossArea - b.grossArea) / a.grossArea < 0.02 && Math.abs(a.footprintArea - b.footprintArea) / a.footprintArea < 0.02;
  const seen = new Set<Typology>();
  for (const o of built) {
    if (seen.has(o.typology) || picked.some((p) => same(p, o))) continue;
    seen.add(o.typology);
    picked.push(o);
  }
  for (const o of built) {
    if (picked.length >= limit) break;
    if (picked.includes(o)) continue;
    // the same wings at another depth, or the same building, is not a new option
    if (picked.some((p) => (p.typology === o.typology && p.along.join() === o.along.join()) || same(p, o))) continue;
    picked.push(o);
  }
  return picked.slice(0, limit).sort((a, b) => b.grossArea - a.grossArea || a.id.localeCompare(b.id));
}
